<template>
  <div>
    <label for="descriptors"
      >Enter Descriptors (comma or space separated):</label
    ><br />
    <textarea
      v-model="inputDescriptors"
      :placeholder="placeholderText"
      rows="4"
      cols="50"
      @input="handleInput"
    ></textarea
    ><br />

    <!-- Table to show live search results with partial matches -->
    <div v-if="partialMatches.length > 0">
      <h3>Live Search Results:</h3>
      <table :style="tableStyle">
        <thead>
          <tr>
            <th :style="headerStyle">Code</th>
            <th :style="headerStyle">Strand</th>
            <th :style="headerStyle">Sub-strand</th>
            <th :style="headerStyle">Descriptor</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="descriptor in partialMatches" :key="descriptor.code">
            <td
              v-html="highlightMatch(descriptor.code)"
              :style="cellStyle"
            ></td>
            <td
              v-html="highlightMatch(descriptor.strand)"
              :style="cellStyle"
            ></td>
            <td
              v-html="highlightMatch(descriptor.sub_strand)"
              :style="cellStyle"
            ></td>
            <td
              v-html="highlightMatch(descriptor.summary)"
              :style="cellStyle"
            ></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
export default {
  data() {
    return {
      inputDescriptors: "", // Start with an empty input
      placeholderText: "VC2EFLE05 VC2EFLY12 VC2EFLY13 VC2CC2Q01", // Placeholder text to guide the user
      allDescriptors: [], // Store all descriptors for live search
      matchedCodes: [], // Store codes that have already been matched
      debounceTimeout: null,
      tableStyle: {
        width: "100%",
        "border-collapse": "collapse",
        "font-family": "Verdana, sans-serif",
        "font-size": "8pt",
        "margin-bottom": "20px",
      },
      headerStyle: {
        padding: "8px",
        border: "1px solid #333",
        "text-align": "left",
        "font-family": "Verdana, sans-serif",
        "font-size": "8pt",
      },
      cellStyle: {
        padding: "8px",
        border: "1px solid #333",
        "text-align": "left",
        "font-family": "Verdana, sans-serif",
        "font-size": "8pt",
      },
    };
  },
  computed: {
    // Computed property to filter and display partial matches
    partialMatches() {
      const lastWord = this.getLastWord(this.inputDescriptors);
      if (!lastWord) return [];

      // Escape special characters in the last word for regex
      const escapedWord = this.escapeRegExp(lastWord);
      const regex = new RegExp(escapedWord, "i");

      // Convert matched codes to lowercase for case-insensitive comparison
      const matchedCodesLower = this.matchedCodes.map((code) =>
        code.toLowerCase()
      );

      // Filter out matched codes from the live search results (case-insensitive)
      return this.allDescriptors
        .filter(
          (descriptor) =>
            !matchedCodesLower.includes(descriptor.code.toLowerCase()) && // Exclude matched codes (case-insensitive)
            (regex.test(descriptor.code) ||
              regex.test(descriptor.strand) ||
              regex.test(descriptor.sub_strand) ||
              regex.test(descriptor.summary))
        )
        .slice(0, 10); // Limit to 10 results
    },
  },
  watch: {
    inputDescriptors: function (newVal) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.lookupDescriptors(newVal);
      }, 300);
    },
  },
  methods: {
    handleInput() {
      if (this.inputDescriptors === "") {
        // If input is cleared, reset initial results with placeholder text
        this.lookupDescriptors(this.placeholderText);
      } else {
        // Perform lookup with actual user input
        this.lookupDescriptors(this.inputDescriptors);
      }
    },
    getLastWord(input) {
      // Extract the last word typed by the user
      const words = input.trim().split(/[\s,]+/);
      return words[words.length - 1] || "";
    },
    escapeRegExp(string) {
      // Escape special characters for use in a regular expression
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
    },
    highlightMatch(text) {
      const lastWord = this.getLastWord(this.inputDescriptors);

      // Ensure text is a valid string
      if (!lastWord || !text) return text || ""; // Return an empty string if text is null or undefined

      // Escape the last word for regex use
      const escapedWord = this.escapeRegExp(lastWord);

      // Use a regex to replace the matching part with a bold version (case-insensitive)
      const regex = new RegExp(`(${escapedWord})`, "gi");
      return text.replace(regex, "<b>$1</b>");
    },
    async lookupDescriptors(input) {
      const descriptorsArray = input.split(/[\s,]+/).filter(Boolean);

      try {
        const response = await fetch("/descriptors.json");
        const data = await response.json();

        const descriptorsData = data.descriptors;

        if (!Array.isArray(descriptorsData)) {
          console.error("Descriptors data is not in an array format.");
          return;
        }

        // Store all descriptors for live search
        this.allDescriptors = descriptorsData;

        // Filter and sort descriptors for the grouped result
        const matchedDescriptors = descriptorsArray
          .map((desc) =>
            descriptorsData.find(
              (d) => d.code.toLowerCase() === desc.toLowerCase()
            )
          )
          .filter(Boolean);

        // Store matched codes to exclude from live search results
        this.matchedCodes = matchedDescriptors.map(
          (descriptor) => descriptor.code
        );

        const sortedDescriptors = matchedDescriptors.sort((a, b) => {
          return (
            a.level.localeCompare(b.level) ||
            a.area.localeCompare(b.area) ||
            a.code.localeCompare(b.code)
          );
        });

        const groupedDescriptors = sortedDescriptors.reduce(
          (acc, descriptor) => {
            const levelAreaKey = `${descriptor.level} ${descriptor.area}`;
            if (!acc[levelAreaKey]) {
              acc[levelAreaKey] = [];
            }
            acc[levelAreaKey].push(descriptor);
            return acc;
          },
          {}
        );

        this.$emit("lookup", groupedDescriptors);
      } catch (error) {
        console.error("Error fetching or parsing JSON:", error);
      }
    },
  },
  mounted() {
    // Trigger lookup for the initial placeholder value
    this.lookupDescriptors(this.placeholderText);
  },
};
</script>
